import { useState, useEffect } from 'react';
import { useApp } from '@/context/AppContext';
import { useAuth } from '@/context/AuthContext';
import { getTimeRemainingShort } from '@/components/shared/CountdownTimer';
import ProjectDetail from '@/components/gestor/ProjectDetail';
import ProductionModule from '@/components/gestor/ProductionModule';
import SalesView from '@/components/gestor/SalesView';
import ManagerSettings from '@/components/gestor/ManagerSettings';
import ManagerQueue from '@/components/gestor/ManagerQueue';
import { LOGO_URL, STATUS_CONFIG } from '@/data/mockData';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Link } from 'react-router-dom';
import { Menu, LayoutGrid, Sparkles, MessageCircle, Settings as SettingsIcon, ListOrdered } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Lightweight XHR helper — fetch is monkey-patched on this platform.
function xhrJsonRaw(url, headers) {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    Object.entries(headers || {}).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.timeout = 10000;
    xhr.onload = () => {
      try { resolve(xhr.status >= 200 && xhr.status < 300 ? JSON.parse(xhr.responseText || '{}') : null); }
      catch { resolve(null); }
    };
    xhr.onerror = () => resolve(null);
    xhr.ontimeout = () => resolve(null);
    xhr.send(null);
  });
}

// Polls /api/sales/conversations every 30s and reports the total unread count.
function useSalesUnread(token) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!token) { setCount(0); return; }
    let stopped = false;
    const headers = { Authorization: `Bearer ${token}` };
    const tick = async () => {
      const data = await xhrJsonRaw(`${API}/api/sales/conversations?_=${Date.now()}`, headers);
      if (stopped) return;
      const total = (data?.conversations || []).reduce((acc, c) => {
        const isUnread = (c.is_read === false) || (c.unread_count || 0) > 0;
        return acc + (isUnread ? 1 : 0);
      }, 0);
      setCount(total);
    };
    tick();
    const interval = setInterval(tick, 30000);
    const onVis = () => { if (document.visibilityState === 'visible') tick(); };
    // Listen for optimistic decrements from SalesView when a conv is marked read.
    const onLocalRead = () => setCount((c) => Math.max(0, c - 1));
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('ruido:sales-conv-read', onLocalRead);
    return () => {
      stopped = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('ruido:sales-conv-read', onLocalRead);
    };
  }, [token]);
  return count;
}

const FILTER_TABS = [
  { key: 'all', label: 'All' },
  { key: 'received', label: 'Received' },
  { key: 'active', label: 'Active' },
  { key: 'delivered', label: 'Delivered' },
];

function ProjectList({ projects, activeId, onSelect, getBrand, filter, setFilter, viewMode, onViewMode, salesUnread = 0 }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => tick(n => n + 1), 60000);
    return () => clearInterval(interval);
  }, []);

  const filteredProjects = projects.filter(p => {
    if (filter === 'all') return true;
    if (filter === 'received') return ['new', 'received'].includes(p.status);
    if (filter === 'active') return ['in_production', 'revision_requested'].includes(p.status);
    if (filter === 'delivered') return ['ready', 'delivered'].includes(p.status);
    return true;
  });

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 flex items-center gap-2.5">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-full overflow-hidden shrink-0">
            <img src={LOGO_URL} alt="Ruido.ai" className="w-full h-full object-cover scale-[1.15]" />
          </div>
          <div>
            <span className="text-sm font-medium text-gray-900 font-['Outfit'] block leading-tight">Ruido<span className="text-gray-400">.ai</span></span>
            <span className="text-[10px] text-gray-400 uppercase tracking-[0.15em]">Manager</span>
          </div>
        </Link>
      </div>

      <Separator className="bg-gray-100" />

      <div className="px-3 pt-3 pb-2">
        <div className="flex gap-1 flex-wrap">
          {FILTER_TABS.map(tab => (
            <button
              key={tab.key}
              data-testid={`filter-${tab.key}`}
              onClick={() => setFilter(tab.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                filter === tab.key
                  ? 'bg-gray-100 text-gray-900'
                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-2 space-y-0.5">
          {filteredProjects.map(project => {
            const needsAttention = !!project.needsAttention && activeId !== project.id;
            return (
            <button
              key={project.id}
              data-testid={`gestor-project-${project.id}`}
              onClick={() => onSelect(project.id)}
              className={`w-full text-left px-3 py-2.5 rounded-lg transition-all relative ${
                activeId === project.id
                  ? 'bg-gray-100'
                  : needsAttention
                    ? 'bg-rose-50 hover:bg-rose-100/80 border border-rose-200'
                    : 'hover:bg-gray-50'
              }`}
            >
              {needsAttention && (
                <span
                  data-testid={`gestor-attention-dot-${project.id}`}
                  className="absolute top-2.5 right-2.5 w-2 h-2 rounded-full bg-rose-500 animate-pulse"
                />
              )}
              <div className="flex items-center justify-between mb-0.5">
                <span
                  className={`text-sm truncate pr-2 ${
                    needsAttention ? 'font-semibold text-rose-900' : 'text-gray-900'
                  }`}
                >
                  {project.name}
                </span>
              </div>
              {needsAttention && (
                <div
                  data-testid={`gestor-attention-label-${project.id}`}
                  className="text-[10px] uppercase tracking-[0.12em] font-medium text-rose-600 mb-1"
                >
                  Client request
                </div>
              )}
              <div className="flex items-center justify-between gap-2">
                {project.status === 'ready' ? (
                  <span className="text-[11px] font-medium text-emerald-600">Ready</span>
                ) : project.status === 'delivered' ? (
                  <span className="text-[11px] font-medium text-emerald-600">Delivered</span>
                ) : project.status === 'revision_requested' ? (
                  <span className="text-[11px] font-medium text-amber-600">In Revision</span>
                ) : (project.status === 'in_production' || project.status === 'received') ? (
                  <span className="text-[11px] font-mono font-semibold animate-breathe-text" style={{ color: '#B8860B' }}>
                    {getTimeRemainingShort(project.deliveryDeadline) || ''}
                  </span>
                ) : (
                  <span className="text-[11px] text-gray-400">{STATUS_CONFIG[project.status]?.label}</span>
                )}
                <span className="text-[11px] text-gray-400 shrink-0">
                  {project.client.name.split(' ')[0]}
                </span>
              </div>
            </button>
            );
          })}
          {filteredProjects.length === 0 && (
            <div className="text-center py-8 text-gray-400 text-sm">No projects match this filter.</div>
          )}
        </div>
      </ScrollArea>

      <Separator className="bg-gray-100" />
      <div className="p-3 space-y-2">
        <button
          data-testid="queue-btn"
          onClick={() => onViewMode?.('queue')}
          className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm transition-all ${
            viewMode === 'queue'
              ? 'bg-gray-900/[0.06] text-gray-900 font-medium'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
          }`}
        >
          <ListOrdered className="w-4 h-4" />
          Queue
        </button>
        <button
          data-testid="production-btn"
          onClick={() => onViewMode?.('production')}
          className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm transition-all ${
            viewMode === 'production'
              ? 'bg-gray-900/[0.06] text-gray-900 font-medium'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
          }`}
        >
          <Sparkles className="w-4 h-4" />
          Production
        </button>
        <button
          data-testid="sales-btn"
          onClick={() => onViewMode?.('sales')}
          className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm transition-all ${
            viewMode === 'sales'
              ? 'bg-gray-900/[0.06] text-gray-900 font-medium'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
          }`}
        >
          <MessageCircle className="w-4 h-4" />
          <span>Sales</span>
          {salesUnread > 0 && (
            <span
              data-testid="sales-sidebar-badge"
              className="ml-auto min-w-[18px] h-[18px] px-1.5 rounded-full bg-rose-500 text-white text-[10px] font-medium flex items-center justify-center"
            >
              {salesUnread > 99 ? '99+' : salesUnread}
            </span>
          )}
        </button>
        <button
          data-testid="manager-settings-btn"
          onClick={() => onViewMode?.('settings')}
          className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm transition-all ${
            viewMode === 'settings'
              ? 'bg-gray-900/[0.06] text-gray-900 font-medium'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
          }`}
        >
          <SettingsIcon className="w-4 h-4" />
          Settings
        </button>
        <span className="block text-center text-[11px] text-gray-400">{filteredProjects.length} {filteredProjects.length !== 1 ? 'projects' : 'project'}</span>
      </div>
    </div>
  );
}

export default function GestorView() {
  const { projects, activeGestorProjectId, setActiveGestorProjectId, getBrand, updateProject } = useApp();
  const { session } = useAuth();
  const salesUnread = useSalesUnread(session?.access_token);
  const [filter, setFilter] = useState('all');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [viewMode, setViewMode] = useState('projects'); // 'projects' | 'production'
  const activeProject = projects.find(p => p.id === activeGestorProjectId);

  const handleSelect = (id) => {
    setActiveGestorProjectId(id);
    setSidebarOpen(false);
    setViewMode('projects');
    // Clear the "needs attention" flag the moment a manager opens the project.
    const proj = projects.find(p => p.id === id);
    if (proj?.needsAttention) updateProject(id, { needsAttention: false });
  };

  return (
    <div className="flex h-screen h-screen-mobile bg-[#f0efed]" data-testid="gestor-view">
      <div className="hidden md:flex w-72 border-r border-gray-200/60 flex-col shrink-0 bg-[#f7f6f4]">
        <ProjectList projects={projects} activeId={activeGestorProjectId} onSelect={handleSelect} getBrand={getBrand} filter={filter} setFilter={setFilter} viewMode={viewMode} onViewMode={setViewMode} salesUnread={salesUnread} />
      </div>

      <div className="md:hidden">
        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <SheetTrigger asChild>
            <button
              data-testid="gestor-mobile-menu-btn"
              className="fixed top-3 left-3 z-50 p-2.5 glass-pill rounded-full transition-colors"
            >
              <Menu className="w-5 h-5 text-gray-500" />
              {salesUnread > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-rose-500 border-2 border-white" />
              )}
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 p-0 bg-[#f7f6f4] border-gray-200/60">
            <ProjectList projects={projects} activeId={activeGestorProjectId} onSelect={handleSelect} getBrand={getBrand} filter={filter} setFilter={setFilter} viewMode={viewMode} onViewMode={(m) => { setViewMode(m); setSidebarOpen(false); }} salesUnread={salesUnread} />
          </SheetContent>
        </Sheet>
      </div>

      <div className="flex-1 flex flex-col min-w-0">
        {viewMode === 'production' ? (
          <ProductionModule />
        ) : viewMode === 'sales' ? (
          <SalesView />
        ) : viewMode === 'settings' ? (
          <ManagerSettings />
        ) : viewMode === 'queue' ? (
          <ManagerQueue
            onOpenProject={(id) => {
              setActiveGestorProjectId(id);
              setViewMode('projects');
              const proj = projects.find(p => p.id === id);
              if (proj?.needsAttention) updateProject(id, { needsAttention: false });
            }}
          />
        ) : activeProject ? (
          <ProjectDetail project={activeProject} onSwitchToProduction={() => setViewMode('production')} />
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center animate-fade-in">
              <LayoutGrid className="w-12 h-12 text-gray-200 mx-auto mb-4" />
              <h2 className="text-xl font-medium text-gray-400 font-['Outfit']">Select a project</h2>
              <p className="text-sm text-gray-400 mt-1">Choose a project from the list to view details.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
